import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useMemo } from 'react';
import { formatDateKey, getCurrentWeekDays } from '../../../../components/calendar/calendar-utils';
import { getLocalToday } from '../../../../config/timezone';
import {
  type INonConformityDocumentService,
  NonConformityDocumentService,
  type NonConformitySourceType,
} from '../../../../services/non-conformity-document/non-conformity-document.service';
import type { CalendarViewMode } from '../../../../stores/calendar-store.factory';
import { getMonthDateRange } from '../calendar-status-helpers';

export const openNonConformityDaysQueryKeys = {
  all: ['non-conformity-open-days'] as const,
  range: (sourceType: string, from: string, to: string) =>
    ['non-conformity-open-days', sourceType, from, to] as const,
};

const EMPTY_DAYS: ReadonlySet<string> = new Set();

/**
 * Day keys (YYYY-MM-DD) in [from, to] holding a non-conformity whose corrective-action
 * document is still missing — the days that must show the warning triangle.
 */
export function useOpenNonConformityDays(
  sourceType: NonConformitySourceType,
  from: string,
  to: string,
): ReadonlySet<string> {
  const service = useInjection<INonConformityDocumentService>(NonConformityDocumentService);

  const { data } = useQuery({
    queryKey: openNonConformityDaysQueryKeys.range(sourceType, from, to),
    queryFn: () => service.findOpenDays(sourceType, from, to),
  });

  return useMemo(() => (data ? new Set(data) : EMPTY_DAYS), [data]);
}

/** Open non-conformity days for whichever calendar view the summary tab is showing. */
export function useOpenNonConformityDaysForView(
  sourceType: NonConformitySourceType,
  viewMode: 'week' | 'month',
  currentMonth: Date,
): ReadonlySet<string> {
  const { fromDate, toDate } = useMemo(() => {
    if (viewMode === 'month') {
      return getMonthDateRange(currentMonth);
    }
    const weekDays = getCurrentWeekDays();
    return { fromDate: formatDateKey(weekDays[0]), toDate: formatDateKey(weekDays[6]) };
  }, [viewMode, currentMonth]);

  return useOpenNonConformityDays(sourceType, fromDate, toDate);
}

/**
 * True when the period the summary tab is showing — the persisted view mode, current
 * week by default — still holds an undocumented non-conformity. The badge must match
 * what the calendar below it displays, not a wider lookback window.
 */
export function useHasOpenNonConformity(
  sourceType: NonConformitySourceType,
  viewMode: CalendarViewMode = 'week',
): boolean {
  const currentMonth = useMemo(() => getLocalToday(), []);

  return useOpenNonConformityDaysForView(sourceType, viewMode, currentMonth).size > 0;
}
